/** Rich-text link tooltip: only web URLs (http/https). */
const PROTOCOL_WHITELIST = new Set(["http", "https"]);

/**
 * Require normal `http://` / `https://` spelling (with `//` after the scheme).
 * WHATWG `new URL("http:a")` still yields `http:` + hostname `a` — we reject the input string
 * unless the user actually typed the slashes (same for `https:x`, `http:example.com`, etc.).
 */
export function linkInputUsesHttpAuthorityPrefix(url: string): boolean {
  const lower = url.toLowerCase();
  return lower.startsWith("http://") || lower.startsWith("https://");
}

/**
 * Normalizes pasted/typed URL text: BOM, NBSP → space, then trim.
 * `String.prototype.trim()` alone does not strip U+00A0 in some environments; other invisible
 * spaces also appear in pasted content.
 */
export function normalizeQuillLinkUrlInput(raw: string): string {
  if (!raw) return "";
  return raw
    .replace(/\uFEFF/g, "")
    .replace(/[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g, " ")
    .trim();
}

/**
 * Returns true if the URL is absolute and uses http or https.
 * Uses the URL parser (not `<a href>`) so bare strings like `provided` are not turned into
 * same-origin paths and incorrectly accepted.
 * Empty / whitespace-only strings are not allowed here; handle clearing the link separately.
 */
export function quillHyperlinkUrlIsAllowed(url: string): boolean {
  const normalized = normalizeQuillLinkUrlInput(url);
  if (!normalized) return false;
  if (!linkInputUsesHttpAuthorityPrefix(normalized)) {
    return false;
  }
  let parsed: URL;
  try {
    parsed = new URL(normalized);
  } catch {
    return false;
  }
  const protocol = parsed.protocol.replace(/:$/, "").toLowerCase();
  return PROTOCOL_WHITELIST.has(protocol);
}

/** Dispatched on `quill.root` when the user tries to save an invalid URL from the link tooltip. */
export const QUILL_LINK_INVALID_EVENT = "fama-quill-link-invalid";

/** Inline error under the link URL field and toast copy when the value is not http(s). */
export const QUILL_LINK_URL_INVALID_HINT =
  "Enter a valid URL using http:// or https://.";
